// LabWorkspacePage - Two-panel lab execution interface
// Left panel: Instructions (Markdown + Quiz + Hints)
// Right panel: Simulated terminal
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  Terminal, ChevronLeft, ChevronRight, Clock,
  CheckCircle2, XCircle, Lightbulb, Award, HelpCircle,
  Square, FlaskConical, Play, Loader2, BookOpen
} from "lucide-react";

// Terminal Component - simulated terminal with command history
function LabTerminal({ sessionId }) {
  const terminalRef = useRef(null);
  const inputRef = useRef(null);
  const [lines, setLines] = useState([
    { type: "system", text: "╔══════════════════════════════════════════════╗" },
    { type: "system", text: "║     🔬 DevOpsLearn Lab Terminal                 ║" },
    { type: "system", text: "║     Type 'help' for available commands       ║" },
    { type: "system", text: "╚══════════════════════════════════════════════╝" },
    { type: "system", text: "" },
  ]);
  const [currentCmd, setCurrentCmd] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [lines]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const executeCommand = async (command) => {
    if (!command.trim()) return;

    setLines(prev => [...prev, { type: "input", text: command }]);
    setHistory(prev => [...prev, command]);
    setHistoryIdx(-1);
    setCurrentCmd("");
    setIsExecuting(true);

    try {
      const res = await api.post("/labs/terminal/execute", {
        session_id: sessionId,
        command: command,
      });
      const output = res.data.output || "";
      if (output) {
        const outputLines = output.split("\n").map(line => ({ type: "output", text: line }));
        setLines(prev => [...prev, ...outputLines]);
      }
    } catch (err) {
      setLines(prev => [...prev, { type: "error", text: `Erreur: ${err.response?.data?.detail || "Échec de l'exécution"}` }]);
    } finally {
      setIsExecuting(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      executeCommand(currentCmd);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length > 0) {
        const newIdx = historyIdx < history.length - 1 ? historyIdx + 1 : historyIdx;
        setHistoryIdx(newIdx);
        setCurrentCmd(history[history.length - 1 - newIdx]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx > 0) {
        const newIdx = historyIdx - 1;
        setHistoryIdx(newIdx);
        setCurrentCmd(history[history.length - 1 - newIdx]);
      } else {
        setHistoryIdx(-1);
        setCurrentCmd("");
      }
    } else if (e.key === "c" && e.ctrlKey) {
      setCurrentCmd("");
      setLines(prev => [...prev, { type: "input", text: currentCmd + "^C" }]);
    } else if (e.key === "l" && e.ctrlKey) {
      e.preventDefault();
      setLines([]);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#1e1e2e] rounded-lg overflow-hidden font-mono text-sm" onClick={() => inputRef.current?.focus()}>
      {/* Terminal header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-[#181825] border-b border-[#313244]">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#f38ba8]" />
          <div className="w-3 h-3 rounded-full bg-[#f9e2af]" />
          <div className="w-3 h-3 rounded-full bg-[#a6e3a1]" />
        </div>
        <span className="text-[#6c7086] text-xs ml-2">lab-terminal — bash</span>
        <Badge variant="outline" className="ml-auto text-[10px] bg-green-500/10 text-green-400 border-green-500/30">
          Connecté
        </Badge>
      </div>

      {/* Terminal content */}
      <div ref={terminalRef} className="flex-1 overflow-y-auto p-3 space-y-0.5 text-[13px] leading-5 min-h-0">
        {lines.map((line, i) => (
          <div key={i} className="whitespace-pre-wrap break-all">
            {line.type === "input" && (
              <span>
                <span className="text-[#a6e3a1]">❯</span>{" "}
                <span className="text-[#89b4fa]">~</span>{" "}
                <span className="text-[#cdd6f4]">{line.text}</span>
              </span>
            )}
            {line.type === "output" && (
              <span className="text-[#cdd6f4]">{line.text}</span>
            )}
            {line.type === "error" && (
              <span className="text-[#f38ba8]">{line.text}</span>
            )}
            {line.type === "system" && (
              <span className="text-[#6c7086]">{line.text}</span>
            )}
          </div>
        ))}

        {/* Input line */}
        <div className="flex items-center">
          <span className="text-[#a6e3a1]">❯</span>{" "}
          <span className="text-[#89b4fa]">~</span>{" "}
          <input
            ref={inputRef}
            type="text"
            value={currentCmd}
            onChange={(e) => setCurrentCmd(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-[#cdd6f4] outline-none border-none ml-1 font-mono text-[13px] caret-[#f5e0dc]"
            autoComplete="off"
            spellCheck={false}
            disabled={isExecuting}
          />
          {isExecuting && <Loader2 className="h-3 w-3 animate-spin text-[#89b4fa] ml-2" />}
        </div>
      </div>
    </div>
  );
}

function QuizComponent({ step, onAnswer, selectedAnswer }) {
  const letters = ["A", "B", "C", "D", "E", "F", "G", "H"];
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-1.5 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-lg">
          <HelpCircle className="h-4 w-4 text-violet-400" />
        </div>
        <span className="text-sm font-semibold text-violet-400">Question à choix unique</span>
      </div>
      
      {step.quiz_options?.map((option, index) => {
        const isSelected = selectedAnswer === index;
        
        // Silent validation UI: only highlight the selected choice in violet, no correct/wrong colors
        let containerClass = "group relative w-full p-0 border-0 rounded-xl text-left transition-all duration-300 overflow-hidden cursor-pointer";
        let innerClass = "flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-300 ";
        let letterClass = "w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm transition-all duration-300 flex-shrink-0 ";
        
        if (isSelected) {
          innerClass += "border-violet-500 bg-violet-500/10 shadow-lg shadow-violet-500/10";
          letterClass += "bg-violet-500 text-white shadow-md shadow-violet-500/20 scale-105";
        } else {
          innerClass += "border-border/50 bg-card hover:border-violet-500/50 hover:bg-violet-500/5 hover:shadow-lg hover:shadow-violet-500/5";
          letterClass += "bg-muted text-muted-foreground group-hover:bg-violet-500/20 group-hover:text-violet-400";
        }
        
        return (
          <button
            key={index}
            className={containerClass}
            onClick={() => onAnswer(index)}
          >
            <div className={innerClass}>
              <div className={letterClass}>
                {letters[index] || (index + 1)}
              </div>
              <span className={`text-sm font-medium flex-1 ${isSelected ? "text-violet-200" : "text-foreground"}`}>
                {option}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function LabWorkspacePage() {
  const { workspaceId, labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lab, setLab] = useState(null);
  const [session, setSession] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizCorrect, setQuizCorrect] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [quizResults, setQuizResults] = useState({}); // stepIndex -> { correct, attempts, answer }
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [labRes, sessionRes] = await Promise.all([
          api.get(`/labs/${labId}`),
          api.post("/labs/sessions", { lab_id: labId, user_id: user?.id, workspace_id: workspaceId }),
        ]);
        setLab(labRes.data);
        setSession(sessionRes.data);
        setCurrentStepIndex(sessionRes.data.current_step || 0);
        
        // Populate quizResults from backend step_results if available
        if (sessionRes.data.step_results) {
          const qRes = {};
          Object.entries(sessionRes.data.step_results).forEach(([idx, data]) => {
            if (data.type === "quiz") {
              // Convert answer index if it's stored as a string number
              let ans = data.answer;
              if (ans !== null && ans !== undefined && ans !== "") {
                ans = parseInt(ans);
              } else {
                ans = null;
              }
              qRes[idx] = { correct: data.correct, attempts: 1, answer: ans };
            }
          });
          setQuizResults(qRes);
        }
        
        // If lab is already completed, show summary automatically
        if (sessionRes.data.status === "completed") {
          setShowSummary(true);
        }
      } catch (err) {
        console.error("Failed to load lab:", err);
        toast.error("Erreur lors du chargement du lab");
        navigate(`/workspace/${workspaceId}/labs`);
      } finally {
        setIsLoading(false);
      }
    };
    if (user?.id) fetchData();
  }, [labId, workspaceId, user?.id, navigate]);

  // Timer
  useEffect(() => {
    const timer = setInterval(() => setElapsedTime(prev => prev + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const currentStep = lab?.steps?.[currentStepIndex];
  const progressPercent = lab?.steps ? ((session?.completed_steps?.length || 0) / lab.steps.length) * 100 : 0;

  const handleValidateStep = async () => {
    if (!session) return;
    try {
      const res = await api.post(`/labs/sessions/${session.id}/validate`, {
        step_index: currentStepIndex,
        answer: quizAnswer?.toString(),
      });
      if (res.data.success) {
        if (currentStep?.step_type !== "quiz") {
          toast.success(res.data.message || "Étape validée !");
        }
        const newCompleted = [...new Set([...(session.completed_steps || []), currentStepIndex])];
        setSession(prev => ({
          ...prev,
          completed_steps: newCompleted,
          score: res.data.score,
          status: res.data.completed ? "completed" : prev.status,
        }));
        if (res.data.completed) {
          toast.success("🎉 Lab terminé ! Félicitations !", { duration: 5000 });
        }
        return true;
      } else {
        if (currentStep?.step_type !== "quiz") {
          toast.error(res.data.message || "Validation échouée");
        }
        return false;
      }
    } catch { toast.error("Erreur lors de la validation"); return false; }
  };

  const handleQuizAnswer = (idx) => {
    setQuizAnswer(idx);
    const isCorrect = idx === currentStep?.quiz_correct_answer;
    // Track their answer silently for the end summary
    setQuizResults(prev => ({ 
      ...prev, 
      [currentStepIndex]: { correct: isCorrect, attempts: 1, answer: idx } 
    }));
  };

  const goNext = async () => {
    if (currentStep?.step_type === "quiz" && quizAnswer === null) {
      toast.error("Veuillez sélectionner une réponse avant de continuer.");
      return;
    }

    const isLastStep = currentStepIndex >= lab.steps.length - 1;

    // Quiet validation applied on goNext for all steps
    if (!session?.completed_steps?.includes(currentStepIndex)) {
      await handleValidateStep();
    }

    if (!isLastStep) {
      setCurrentStepIndex(prev => prev + 1);
      setQuizAnswer(null);
      setQuizCorrect(null);
      setQuizAttempts(0);
      setShowHint(false);
    } else {
      // Last step — show summary if there are quiz results
      const quizSteps = lab.steps.filter(s => s.step_type === "quiz");
      if (quizSteps.length > 0) {
        setShowSummary(true);
      }
    }
  };

  const goPrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setQuizAnswer(null);
      setQuizCorrect(null);
      setQuizAttempts(0);
      setShowHint(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <FlaskConical className="h-12 w-12 text-primary mx-auto animate-pulse" />
          <p className="text-muted-foreground">Chargement du lab...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-background">
      {/* Header bar */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm flex-shrink-0">
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/workspace/${workspaceId}/labs`)} className="text-muted-foreground">
              <ChevronLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <div className="h-5 w-px bg-border" />
            <FlaskConical className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm truncate max-w-[300px]">{lab?.title}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Step indicators */}
            <div className="flex gap-1">
              {lab?.steps?.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => { setCurrentStepIndex(idx); setQuizAnswer(null); setQuizCorrect(null); setShowHint(false); }}
                  className={`w-7 h-1.5 rounded-full transition-colors cursor-pointer ${
                    session?.completed_steps?.includes(idx)
                      ? "bg-green-500"
                      : idx === currentStepIndex
                      ? "bg-primary"
                      : "bg-muted"
                  }`}
                  title={`Étape ${idx + 1}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-mono text-xs">{formatTime(elapsedTime)}</span>
            </div>

            <Badge variant="outline" className="font-mono text-xs">
              Score: {session?.score || 0}
            </Badge>

            <Button variant="destructive" size="sm" onClick={() => navigate(`/workspace/${workspaceId}/labs`)}>
              <Square className="h-3.5 w-3.5 mr-1" /> Quitter
            </Button>
          </div>
        </div>
      </header>

      {/* Two-panel layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left Panel - Instructions */}
        <div className="w-[45%] border-r border-border flex flex-col bg-card min-h-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
            <Badge variant="outline" className="font-mono text-xs">
              Étape {currentStepIndex + 1}/{lab?.steps?.length}
            </Badge>
            <div className="flex items-center gap-2">
              {session?.completed_steps?.includes(currentStepIndex) && (
                <Badge className="bg-green-500/20 text-green-400 text-xs">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Validée
                </Badge>
              )}
              {currentStep?.step_type === "task" && (
                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                  ⚡ Tâche
                </Badge>
              )}
              {currentStep?.step_type === "quiz" && (
                <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                  ❓ Quiz
                </Badge>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 min-h-0">
            {/* Markdown content with Premium Styling Blocks */}
            <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border shadow-sm relative overflow-hidden group">
              <div className="absolute -top-6 -right-6 p-4 opacity-5 rotate-12 pointer-events-none transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500">
                {currentStep?.step_type === "task" ? <Terminal className="w-40 h-40" /> : <BookOpen className="w-40 h-40" />}
              </div>
              
              <h3 className="text-[11px] font-bold text-violet-500 mb-5 flex items-center gap-2 uppercase tracking-wider">
                {currentStep?.step_type === "task" ? (
                  <><span className="flex h-5 w-5 bg-violet-500/20 text-violet-500 items-center justify-center rounded-md"><FlaskConical className="w-3 h-3"/></span> Exercice Pratique</>
                ) : currentStep?.step_type === "quiz" ? (
                  <><span className="flex h-5 w-5 bg-purple-500/20 text-purple-500 items-center justify-center rounded-md"><HelpCircle className="w-3 h-3"/></span> Question pour valider</>
                ) : (
                  <><span className="flex h-5 w-5 bg-blue-500/20 text-blue-500 items-center justify-center rounded-md"><BookOpen className="w-3 h-3"/></span> Théorie & Instructions</>
                )}
              </h3>
              
              <div className="prose prose-violet dark:prose-invert max-w-none relative z-10">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent mb-5" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-lg font-bold text-foreground mt-8 mb-4 border-b border-border pb-2 flex items-center gap-2" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-base font-semibold text-foreground/90 mt-6 mb-3" {...props} />,
                    p: ({node, ...props}) => <p className="text-muted-foreground leading-relaxed mb-5 text-[15px]" {...props} />,
                    ul: ({node, ...props}) => <ul className="space-y-3 mb-6 ml-1" {...props} />,
                    li: ({node, ...props}) => (
                      <li className="flex gap-3 items-start group">
                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-violet-500/50 group-hover:scale-150 group-hover:bg-violet-500 transition-all" />
                        <span className="text-muted-foreground text-[15px]" {...props} />
                      </li>
                    ),
                    a: ({node, ...props}) => <a className="text-violet-400 font-medium hover:text-violet-300 underline underline-offset-4 decoration-violet-500/30 transition-all" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-foreground bg-violet-500/10 px-1 rounded-sm" {...props} />,
                    code: ({node, inline, className, children, ...props}) => {
                      const match = /language-(\w+)/.exec(className || '');
                      if (inline) {
                        return <code className="bg-muted text-foreground px-1.5 py-0.5 rounded font-mono text-[13px] border border-border" {...props}>{children}</code>;
                      }
                      return (
                        <div className="relative group my-6 overflow-hidden rounded-xl border border-border/50 bg-[#1e1e2e] shadow-xl shadow-black/20">
                          <div className="flex items-center justify-between px-4 py-2 bg-[#11111b] border-b border-white/5">
                            <div className="flex gap-1.5">
                              <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                              <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                              <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono bg-white/5 px-2 py-0.5 rounded-full">
                              {match ? match[1] : 'commande'}
                            </span>
                          </div>
                          <div className="p-4 overflow-x-auto text-left">
                            <code className="font-mono text-[13px] text-[#cdd6f4] leading-relaxed block" {...props}>
                              {children}
                            </code>
                          </div>
                        </div>
                      );
                    },
                    blockquote: ({node, ...props}) => (
                      <div className="my-6 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20 relative overflow-hidden">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-indigo-500" />
                        <div className="flex gap-3">
                          <div className="mt-0.5 text-blue-500"><Lightbulb className="h-5 w-5" /></div>
                          <div className="text-blue-700 dark:text-blue-300 text-[14px] leading-relaxed" {...props} />
                        </div>
                      </div>
                    )
                  }}
                >
                  {currentStep?.content || ""}
                </ReactMarkdown>
              </div>
            </div>

            {/* Quiz */}
            {currentStep?.step_type === "quiz" && (
              <QuizComponent
                step={currentStep}
                onAnswer={handleQuizAnswer}
                selectedAnswer={quizAnswer}
              />
            )}

            {/* Hint */}
            {currentStep?.hint && (
              <div className="mt-6">
                <Button variant="outline" size="sm" onClick={() => setShowHint(!showHint)} className="gap-2 text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10">
                  <Lightbulb className="h-4 w-4" />
                  {showHint ? "Masquer l'indice" : "Afficher l'indice"}
                </Button>
                {showHint && (
                  <Card className="mt-3 bg-yellow-500/5 border-yellow-500/20">
                    <CardContent className="p-3">
                      <p className="text-sm text-yellow-500">{currentStep.hint}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>

          {/* Navigation footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <Button variant="outline" onClick={goPrev} disabled={currentStepIndex === 0} size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
            </Button>

            {session?.status === "completed" ? (
              <Button onClick={() => setShowSummary(true)} size="sm" className="gap-2 bg-gradient-to-r from-violet-600 to-purple-600">
                <Award className="h-4 w-4" /> Voir le résultat
              </Button>
            ) : (
              <>
                {currentStep?.step_type === "task" && !session?.completed_steps?.includes(currentStepIndex) && (
                  <Button onClick={async () => {
                    const ok = await handleValidateStep();
                    // If validated and it's the last step, completion is auto-handled
                  }} size="sm" className="gap-2 bg-gradient-to-r from-cyan-600 to-blue-600">
                    <CheckCircle2 className="h-4 w-4" /> Valider
                  </Button>
                )}

                {currentStep?.step_type === "instruction" && (
                  <Button onClick={goNext} size="sm" className={`gap-2 ${
                    currentStepIndex >= (lab?.steps?.length || 0) - 1
                      ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                      : ""
                  }`}>
                    {currentStepIndex >= (lab?.steps?.length || 0) - 1 ? "✅ Terminer le Lab" : "Suivant"}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}

                {currentStep?.step_type === "quiz" && quizAnswer !== null && (
                  <Button onClick={goNext} size="sm" className={`gap-2 ${
                    currentStepIndex >= (lab?.steps?.length || 0) - 1
                      ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                      : "bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600"
                  }`}>
                    {currentStepIndex >= (lab?.steps?.length || 0) - 1 ? "✅ Terminer le Lab" : "Suivant"}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}

                {currentStep?.step_type === "task" && session?.completed_steps?.includes(currentStepIndex) && (
                  <Button onClick={goNext} size="sm" className={`gap-2 ${
                    currentStepIndex >= (lab?.steps?.length || 0) - 1
                      ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                      : ""
                  }`}>
                    {currentStepIndex >= (lab?.steps?.length || 0) - 1 ? "✅ Terminer le Lab" : "Suivant"}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel - Terminal */}
        <div className="flex-1 p-3 bg-[#11111b] min-h-0">
          {session && (
            <LabTerminal sessionId={session.id} />
          )}
        </div>
      </div>

      {/* Completion Summary Dialog */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-card border border-border rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-xl">
                  <Award className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Résumé du Lab</h2>
                  <p className="text-sm text-muted-foreground">{lab?.title}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Global Score */}
              {(() => {
                const allSteps = lab?.steps || [];
                const totalSteps = allSteps.length;
                const correctCount = allSteps.filter((step, i) => {
                  if (step.step_type === "quiz") {
                    return quizResults[i]?.correct;
                  }
                  return session?.completed_steps?.includes(i);
                }).length;
                const percent = totalSteps > 0 ? Math.round((correctCount / totalSteps) * 100) : 0;
                
                return (
                  <>
                    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-pink-500/10 border border-violet-500/20">
                      <div className="text-5xl font-black bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                        {percent}%
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {correctCount}/{totalSteps} étapes validées
                      </p>
                      <div className="w-full h-2 bg-muted rounded-full mt-3 overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 ${
                            percent >= 80 ? "bg-gradient-to-r from-green-500 to-emerald-500" :
                            percent >= 50 ? "bg-gradient-to-r from-amber-500 to-yellow-500" :
                            "bg-gradient-to-r from-red-500 to-rose-500"
                          }`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <Badge className={`mt-3 ${
                        percent >= 80 ? "bg-green-500/20 text-green-400" :
                        percent >= 50 ? "bg-amber-500/20 text-amber-400" :
                        "bg-red-500/20 text-red-400"
                      }`}>
                        {percent >= 80 ? "🌟 Excellent" : percent >= 50 ? "👍 Passable" : "📚 À revoir"}
                      </Badge>
                    </div>

                    {/* Per-question results */}
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-muted-foreground">Détail des étapes</h3>
                      {allSteps.map((qStep, qi) => {
                        const stepIdx = qi;
                        const isQuiz = qStep.step_type === "quiz";
                        
                        let isRight = false;
                        let result = null;
                        
                        if (isQuiz) {
                          result = quizResults[stepIdx];
                          isRight = result?.correct;
                        } else {
                          isRight = session?.completed_steps?.includes(stepIdx);
                          if (isRight) {
                             result = { correct: true, points: 10 };
                          }
                        }

                        return (
                          <div key={qi} className={`p-3 rounded-lg border flex items-start gap-3 ${
                            isRight 
                              ? "bg-green-500/5 border-green-500/20" 
                              : result ? "bg-red-500/5 border-red-500/20" : "bg-muted/30 border-border"
                          }`}>
                            <div className={`p-1 rounded-md flex-shrink-0 mt-0.5 ${
                              isRight ? "bg-green-500/20" : result ? "bg-red-500/20" : "bg-muted"
                            }`}>
                              {isRight ? (
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                              ) : result ? (
                                <XCircle className="h-4 w-4 text-red-400" />
                              ) : (
                                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">Étape {qi + 1}. {qStep.title}</p>
                              {result && isQuiz && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {isRight ? "Correcte" : "Incorrecte"}
                                </p>
                              )}
                              {isRight && !isQuiz && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Validée
                                </p>
                              )}
                            </div>
                            <Badge className={`flex-shrink-0 text-[10px] ${
                              isRight ? "bg-green-500/20 text-green-400" : 
                              result ? "bg-red-500/20 text-red-400" : "bg-muted text-muted-foreground"
                            }`}>
                              {isRight ? "+10 pts" : result ? "0 pts" : "—"}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>

                    {/* Score total */}
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
                      <span className="text-sm font-medium">Score total</span>
                      <span className="text-lg font-bold font-mono">{session?.score || 0} pts</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="p-4 border-t border-border flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowSummary(false); }}
              >
                Fermer le résumé
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                onClick={() => navigate(`/workspace/${workspaceId}/labs`)}
              >
                Retour au catalogue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
